import { Injectable } from '@angular/core';
import { ReferralStatus, UrgencyType } from '@app/api-module/control-centre-api-client';
import { ModalityType } from '@app/shared/model/shared.model';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import * as ReferralFiltersActions from './referral-filters.actions';
import { DateTime, Interval } from 'luxon';

export interface ReferralFiltersStateModel {
    filteredModalities: ModalityType[];
    filteredUrgencies: UrgencyType[];
    filteredStatuses: ReferralStatus[];

    timeRangeFilter: Interval;
}

@State<ReferralFiltersStateModel>({
    name: 'referralFilters',
    defaults: {
        filteredModalities: [],
        filteredUrgencies: [],
        filteredStatuses: [],

        timeRangeFilter: ReferralFiltersState.defaultTimeRange
    }
})
@Injectable()
export class ReferralFiltersState {
    public static defaultTimeRange: Interval = Interval.fromDateTimes(
        DateTime.now().minus({ days: 3 }).startOf('day'),
        DateTime.now().endOf('day')
    );

    @Action(ReferralFiltersActions.UpdateFilters)
    updateFilters(context: StateContext<ReferralFiltersStateModel>, action: ReferralFiltersActions.UpdateFilters): void {
        context.patchState({
            filteredModalities: [...action.modalities],
            filteredUrgencies: [...action.urgencies],
            filteredStatuses: [...action.statuses]
        });
    }

    @Action(ReferralFiltersActions.UpdateTimeRangeFilter)
    updateTimeRangeFilter(context: StateContext<ReferralFiltersStateModel>, action: ReferralFiltersActions.UpdateTimeRangeFilter): void {
        context.patchState({
            timeRangeFilter: action.timeRangeFilter
        });
    }

    @Action(ReferralFiltersActions.ClearFilters)
    clearFilters(context: StateContext<ReferralFiltersStateModel>, _action: ReferralFiltersActions.ClearFilters): void {
        context.setState({
            filteredModalities: [],
            filteredUrgencies: [],
            filteredStatuses: [],

            timeRangeFilter: ReferralFiltersState.defaultTimeRange
        });
    }

    @Selector()
    static filtersApplied(state: ReferralFiltersStateModel): boolean {
        return (
            state.filteredModalities.length > 0 ||
            state.filteredUrgencies.length > 0 ||
            state.filteredStatuses.length > 0 ||
            !state.timeRangeFilter.equals(ReferralFiltersState.defaultTimeRange)
        );
    }

    @Selector()
    static getFilteredModalities(state: ReferralFiltersStateModel): ModalityType[] {
        return state.filteredModalities;
    }

    @Selector()
    static getFilteredUrgencies(state: ReferralFiltersStateModel): UrgencyType[] {
        return state.filteredUrgencies;
    }

    @Selector()
    static getFilteredStatuses(state: ReferralFiltersStateModel): ReferralStatus[] {
        return state.filteredStatuses;
    }

    @Selector()
    static getTimeRangeFilter(state: ReferralFiltersStateModel): Interval {
        return state.timeRangeFilter;
    }
}
